import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import BackendService from '../services/backendService';
import { PresentationDTO } from '../services/backendService/types';
import { Template } from '../services/TemplateService/Template.type';
import { convertTemplateDataTypeToPresentationDTO } from '../utils/templateUtils';
import useAutosaveTemplate from './useAutosaveTemplate';
import { currentPresentationsCacheKey, presentationsCacheKey } from './usePresentations';
import useSaveFolder from './useSaveFolder';
import { workspaceFoldersCacheKey } from './useWorkspaceFolders';

type PresentationsInfiniteData = InfiniteData<{
  items: PresentationDTO[];
  cursor?: string;
}>;

export const upsertPresentationToTop = (
  prevData: PresentationsInfiniteData | undefined,
  presentation: PresentationDTO,
): PresentationsInfiniteData => {
  if (!prevData) {
    return {
      pages: [{ items: [presentation], cursor: undefined }],
      pageParams: [undefined],
    };
  }

  const pagesWithoutPresentation = prevData.pages.map(page => ({
    ...page,
    items: page.items.filter(item => item.id !== presentation.id),
  }));

  const [firstPage, ...restPages] = pagesWithoutPresentation;

  if (!firstPage) {
    return {
      ...prevData,
      pages: [{ items: [presentation], cursor: undefined }],
      pageParams: [undefined],
    };
  }

  return {
    ...prevData,
    pages: [{ ...firstPage, items: [presentation, ...firstPage.items] }, ...restPages],
  };
};

function useMoveToFolder() {
  const { autoSaveTemplate, isSavingTemplate } = useAutosaveTemplate();

  const { isSavingFolder, saveFolder } = useSaveFolder();
  const client = useQueryClient();
  const moveLesson = useCallback(
    ({
      id,
      destinationFolderId,
      isPrivate,
      onSuccessFunction,
    }: {
      destinationFolderId: string | null;
      id: string;
      isPrivate: boolean;
      onSuccessFunction: () => void;
    }) => {
      autoSaveTemplate({
        onSuccessFunction: async (data: Template) => {
          const newCacheKey = currentPresentationsCacheKey({
            parentId: destinationFolderId ?? undefined,
            isPrivate: isPrivate,
            orderBy: 'modifiedAt',
            sortDirection: 'desc',
          });

          // Invalidate relevant caches immediately on successful mutation.
          await Promise.all([
            client.invalidateQueries({
              queryKey: ['templates']
            }),
            client.invalidateQueries({
              queryKey: [presentationsCacheKey]
            }),
            client.invalidateQueries({
              queryKey: ['workspaceTemplates']
            }),
            client.invalidateQueries({
              queryKey: newCacheKey
            }),
          ]);

          /* SIDE EFFECT: Ensure destination cache exists before upsert */
          await client.fetchInfiniteQuery({
            queryKey: newCacheKey,
            queryFn: ({ pageParam }: { pageParam?: string }) =>
              BackendService.instance.presentations.list({
                isPrivate,
                cursor: pageParam,
                limit: 10,
                orderBy: 'modifiedAt',
                parentId: destinationFolderId ?? undefined,
                sortDirection: 'desc',
              }),
            initialPageParam: undefined as string | undefined,
          });

          // Now add the item to the top of the first page
          client.setQueryData<PresentationsInfiniteData>(newCacheKey, prevData => {
            const presentationDTOs = convertTemplateDataTypeToPresentationDTO([data]);
            return upsertPresentationToTop(prevData, presentationDTOs[0]);
          });

          onSuccessFunction();
        },
        templateConfig: {
          id,
          isPrivate,
          modifiedAt: new Date().getTime(),
          parentId: destinationFolderId,
        },
      });
    },
    [autoSaveTemplate, client],
  );

  const moveFolder = useCallback(
    ({
      onSuccessFunction,
      id,
      isPrivate,
      destinationFolderId,
    }: {
      id: string;
      isPrivate: boolean;
      destinationFolderId: string | null;
      onSuccessFunction: () => void;
    }) => {
      saveFolder({
        folderConfig: {
          id,
          isPrivate,
          parentId: destinationFolderId,
        },
        onSuccessFunction: () => {
          client.invalidateQueries({
            queryKey: [workspaceFoldersCacheKey]
          });
          client.invalidateQueries({
            queryKey: ['folders']
          });
          onSuccessFunction();
        },
      });
    },
    [saveFolder, client],
  );

  return {
    moveFolder,
    moveLesson,
    isMoving: isSavingTemplate || isSavingFolder,
  };
}

export default useMoveToFolder;
